import { api } from './api';

const basePath = ({ organizationId, projectId, resourceId }) => (
  `/v1/organizations/${encodeURIComponent(organizationId)}`
  + `/projects/${encodeURIComponent(projectId)}`
  + `/resources/${encodeURIComponent(resourceId)}`
);

export const retirementCapabilitiesPath = (scope) => `${basePath(scope)}/retirement-capabilities`;
export const inventoryRetirementPath = (scope) => `${basePath(scope)}/inventory-retirements`;
export const providerDeletionPath = (scope) => `${basePath(scope)}/provider-deletion-requests`;
export const retirementOperationPath = (scope, operationId) => `/v1/organizations/${encodeURIComponent(scope.organizationId)}/projects/${encodeURIComponent(scope.projectId)}/operations/${encodeURIComponent(operationId)}`;
export const newRetirementIntentKey = () => window.crypto?.randomUUID?.() || `retirement-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const retirementApi = {
  capabilities: (scope) => api.get(retirementCapabilitiesPath(scope)),
  operation: (scope, operationId) => api.get(retirementOperationPath(scope, operationId)),
  retireInventory: (scope, reason, idempotencyKey) => api.post(
    inventoryRetirementPath(scope), { reason }, { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  requestProviderDeletion: (scope, reason, billingAcknowledged, idempotencyKey) => api.post(
    providerDeletionPath(scope),
    { reason, billing_end_acknowledged: billingAcknowledged },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
};

export const normalizeRetirementCapabilities = (payload = {}) => ({
  resource_id: payload.resource_id,
  lifecycle_status: payload.lifecycle_status || 'active',
  inventory: {
    allowed: payload.inventory?.can_retire === true,
    reason_code: payload.inventory?.reason_code || null,
  },
  provider: {
    allowed: payload.provider_deletion?.can_request === true,
    reason_code: payload.provider_deletion?.reason_code || null,
    provider_type: payload.provider_deletion?.provider_type,
    completion_semantics: payload.provider_deletion?.completion_semantics || 'authoritative_not_found',
    billing_end_authoritative: payload.provider_deletion?.billing_end_authoritative === true,
    warning: payload.provider_deletion?.warning || null,
  },
});

export const retirementDenialCopy = (reason, intent) => ({
  already_retired: 'This resource is already retired from active inventory.',
  forbidden: intent === 'inventory' ? 'Your role cannot retire inventory records in this project.' : 'Your role cannot request provider deletion in this project.',
  feature_disabled: 'Provider deletion is disabled by the Cloud Watcher operator.',
  provider_unavailable: 'The provider connection is unavailable. Inventory retirement is still available.',
  unsupported: 'This provider adapter cannot safely delete this resource type.',
  missing_provider_identity: 'This record has no authoritative provider identity, so provider deletion is blocked.',
  already_pending: 'A governed provider deletion is already pending for this resource.',
  already_deleted: 'The provider has already authoritatively reported this resource as deleted.',
}[reason] || (intent === 'inventory' ? 'Inventory retirement is unavailable.' : 'Provider deletion is unavailable.'));

export const retirementProgress = ({ lifecycleStatus, request, operation } = {}) => {
  const operationStatus = operation?.status;
  const requestStatus = request?.status;
  if (operationStatus === 'indeterminate' || operationStatus === 'outcome_indeterminate' || operation?.provider_presence === 'unknown') {
    return { kind: 'indeterminate', label: 'Provider outcome unknown', detail: 'Cloud Watcher retained the record and will keep reconciling. Do not submit another deletion.' };
  }
  if (['failed', 'rejected', 'cancelled', 'expired'].includes(operationStatus) || ['failed', 'rejected', 'cancelled', 'expired'].includes(requestStatus)) {
    return { kind: 'failed', label: 'Deletion did not complete', detail: 'The inventory record was retained. Open My Work for the exact failure and safe retry guidance.' };
  }
  if (lifecycleStatus === 'deleted' || operation?.provider_presence === 'not_found' || operationStatus === 'succeeded') {
    return { kind: 'complete', label: 'Provider deletion confirmed', detail: 'The provider authoritatively reported the resource absent. Cloud Watcher retained a deleted history record.' };
  }
  if (operation?.cancellation_scheduled_at || operationStatus === 'cancellation_scheduled') {
    return { kind: 'scheduled', label: 'Cancellation scheduled', detail: operation?.billing_end_at ? `Provider billing is scheduled to end at ${operation.billing_end_at}.` : 'The provider scheduled cancellation for the end of its billing period.' };
  }
  if (['deletion_pending', 'deleting'].includes(lifecycleStatus) || ['submitted', 'awaiting_approval', 'approved', 'queued', 'running', 'reconciling'].includes(requestStatus) || operationStatus) {
    return { kind: 'pending', label: 'Deletion request in progress', detail: 'Cloud Watcher will retain this record while approval, provider submission, and reconciliation complete.' };
  }
  return null;
};

export const normalizeRetirementOperation = (operation) => {
  if (!operation) return null;
  const details = operation.provider_outcome || operation.outcome || operation.provider_receipt?.details || {};
  return { ...details, ...operation };
};

export const normalizeDeletionResult = (payload = {}) => ({
  request: payload.request || payload.requisition || null,
  operation: normalizeRetirementOperation(payload.operation || payload.request?.operation),
  replayed: payload.replayed === true,
});

export const requiresBillingEndAcknowledgement = (provider = {}) => provider.provider_type === 'contabo'
  || provider.completion_semantics === 'cancellation_scheduled'
  || provider.billing_end_authoritative === true;

export const retirementConfirmationMatches = (entered, resourceName) => String(entered || '').trim() === String(resourceName || '');

export const retirementControlsUnavailableMessage = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const code = typeof detail === 'object' ? detail?.code : null;
  const message = typeof detail === 'string' ? detail : detail?.message;
  if (status === 403) {
    return message || 'Retirement controls are unavailable for your role.';
  }
  if (status === 503 && code === 'retirement_unavailable') {
    return message || 'Retirement controls are disabled in this environment.';
  }
  return null;
};
